/**
 * Centralised API client for Samudyoga frontend.
 *
 * - Base URL comes from REACT_APP_API_BASE (set via .env / build-time).
 * - Falls back to the production host if unset (no hardcoded creds — this is
 *   just a public URL).
 * - Wraps fetch with JSON handling, Authorization header injection, and a
 *   401-triggered local-logout redirect to /signin.
 */

export const API_BASE =
    process.env.REACT_APP_API_BASE ||
    'https://backend.samudyoga.swadharmaservices.in';

const getToken = () => localStorage.getItem('token');

const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('email');
    window.dispatchEvent(new Event('storage'));
};

async function request(path, { method = 'GET', body, headers = {}, raw = false } = {}) {
    const init = {
        method,
        headers: {
            Accept: 'application/json',
            ...(body && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
            ...headers,
        },
    };

    const token = getToken();
    if (token) init.headers.Authorization = `Bearer ${token}`;

    if (body instanceof FormData) {
        init.body = body;
    } else if (body !== undefined) {
        init.body = JSON.stringify(body);
    }

    let response;
    try {
        response = await fetch(`${API_BASE}${path}`, init);
    } catch (e) {
        throw new ApiError('Network error — could not reach the server.', 0);
    }

    if (response.status === 401) {
        // Token expired or missing — force re-login.
        clearSession();
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/signin')) {
            window.location.href = '/signin';
        }
        throw new ApiError('Please sign in again.', 401);
    }

    if (raw) return response;

    const text = await response.text();
    const data = text ? safeJson(text) : null;

    if (!response.ok) {
        const message = (data && (data.error || data.message)) || `Request failed (${response.status})`;
        throw new ApiError(message, response.status, data);
    }
    return data;
}

function safeJson(text) {
    try { return JSON.parse(text); } catch { return text; }
}

export class ApiError extends Error {
    constructor(message, status, body = null) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

export const api = {
    get:   (path)       => request(path),
    post:  (path, body) => request(path, { method: 'POST',   body }),
    patch: (path, body) => request(path, { method: 'PATCH',  body }),
    del:   (path)       => request(path, { method: 'DELETE' }),
    raw:   (path, opts) => request(path, { ...opts, raw: true }),
};

export default api;
